import React from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, FourOhFourErrorPage, SurveyTheme } from '@sm/webassets';
import { t } from '@sm/intl';

import Layout from '~app/components/Survey/Layout';
import { SurveyTitle } from '~app/components/Survey/SurveyTitleV2';
import SurveyFooter from '~app/components/Survey/FooterContainer';
import { PageTitle } from '~app/components/Survey/PageTitle';
import { PageDescription } from '~app/components/Survey/PageDescription';

import { getSurveyIdFromCollector, getSurveyDetails } from './SurveyTaking.graphql';
import displayQuestionNumber from './helper/displayQuestionNumber';
import createHeadingLevel from './helper/createHeadingLevel';
import useStyles from './useStyles';

const COPY = {
  META_DESCRIPTION: {
    id: 'SurveyTaking.metaDescription',
    defaultMessage:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
};

const SurveyTaking = () => {
  //
  /**
   * TODO: get url from StaticContext, convert slug to collectorId.
   *
   * '55804393' for @see https://www.monkeytest3.com/rx/77JC2ZQ
   * '510967475' for @see https://www.monkeytest1.com/rx/P8VKGS9
   */
  const collectorId = '510967475';

  const {
    loading: loadingCollector,
    error: errorCollector,
    data: collectorData,
  } = useQuery(getSurveyIdFromCollector, {
    variables: {
      collectorId,
    },
  });

  if (errorCollector) {
    throw errorCollector;
  }

  const {
    loading: loadingSurvey,
    error: errorSurvey,
    data: surveyData,
  } = useQuery(getSurveyDetails, {
    skip: !collectorData || loadingCollector,
    variables: {
      surveyId: collectorData?.collector?.surveyId,
    },
  });

  const {
    surveyHeaderContainer,
    surveyToolbarContainer,
    surveyLogoContainer,
    surveyTitleContainer,
    pageHeaderContainer,
    questionContainer,
    pageFooterContainer,
  } = useStyles();

  if (errorSurvey) {
    throw errorSurvey;
  }

  if (!loadingSurvey && surveyData.survey === null) {
    return <FourOhFourErrorPage />;
  }

  if (surveyData) {
    const surveyId = collectorData?.collector?.surveyId;
    const surveyTitle = surveyData?.survey?.title;
    const surveyTitleHTML = surveyData?.survey?.titleHTML;
    const questions = surveyData?.survey?.questionsQM?.items ?? [];
    const langCode = surveyData?.survey?.language?.code;
    const isRTL = ['ar', 'he', 'fa'].indexOf(langCode) > -1;

    /**
     * Design Vars
     * design and layout related variables
     */
    const {
      footer: { enabled: isFooterEnabled, grayscale },
      surveyTitle: { enabled: isSurveyTitleEnabled },
      showPageTitles,
      showPageNumbers,
      logo: { enabled: isLogoEnabled },
      questionNumbering: { enabled: isQuestionNumberingEnabled },
      // MISSING:  isExitLinkEnabled - N/A in Query
    } = surveyData?.survey?.design;

    /**
     * Pages Vars
     * The data below may be consumed and calculated via
     * a `SurveyProvider` or `PageProvider`
     */
    const surveyPages = surveyData?.survey?.pages?.items || [];
    const currentPage = 0;

    /**
     * Page Header
     * calculate proper Page Title and Description based on current page
     */
    const { heading: pageHeading, subHeading: pageDescription } = surveyPages[currentPage];
    const pageTitle = showPageNumbers ? `${currentPage + 1}. ${pageHeading}` : pageHeading;
    const hasPageDescription = !!pageDescription;

    const nextHeadingElement = createHeadingLevel();

    // TODO: use react-jss for theme in Header/Footer components when replacing
    return (
      <BasePage
        actionFlow="SurveyTaking"
        pageId="SurveyTaking"
        legacyWeb="respweb"
        includeHeader={false}
        includeFooter={false}
      >
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{surveyTitle} Survey</title>
          <meta name="description" content={t(COPY.META_DESCRIPTION)} />
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
          <meta httpEquiv="content-language" content={langCode} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="surveyTaking">
          {/* <SurveyProvider> */}
          <Layout isRTL={isRTL}>
            {/* <PageProvider> */}
            {/* <ProgressionProvider> */}
            {/**
             * NOTE: This header layout (below) is ok for most `theme.layout` types, except for
             * the `BACKGROUND_PARTIAL_TOP_BANNER`, where the ExitLink, must be below
             * the header in flow, since the layout splits at the first child in this case.
             * The Layout component may have to accomodate this, or a new component made to
             * account for this. 🤔
             */}
            <header id={`survey-header-container-${surveyId}`} className={surveyHeaderContainer}>
              {/* <If condition={isExitLinkEnabld}> */}
              <div id={`survey-toolbar-${surveyId}`} className={surveyToolbarContainer}>
                EXIT-LINK PLACEHOLDER
              </div>
              {/* </If> */}
              <If condition={isLogoEnabled}>
                <div id={`survey-logo-container-${surveyId}`} className={surveyLogoContainer}>
                  LOGO PLACEHOLDER
                </div>
              </If>
            </header>
            <main>
              <div id={`survey-title-container-${surveyId}`} className={surveyTitleContainer}>
                <If condition={isSurveyTitleEnabled}>
                  <SurveyTitle element={nextHeadingElement(0)} text={surveyTitleHTML} />
                </If>
                {/* <SurveyIntro text={surveyIntro} /> */}
                {/* <OKButton aria-label="Continue" /> */}
              </div>

              <header id={`page-header-containier-${surveyId}`} className={pageHeaderContainer}>
                <If condition={showPageTitles}>
                  <PageTitle element={nextHeadingElement(1)} text={pageTitle} />
                </If>
                <If condition={hasPageDescription}>
                  <PageDescription text={pageDescription} />
                </If>
                {/* <OKButton aria-label="Start the Survey" /> */}
              </header>

              <form id={`form-${surveyId}`} className={questionContainer}>
                {questions.map(q => {
                  const {
                    id,
                    position: { inPage },
                    heading: {
                      default: { text },
                    },
                  } = q;
                  const qID = `question-${id}`;
                  return (
                    <div id={qID} key={id}>
                      <label id={`${qID}-label`} htmlFor={`${qID}-input`}>
                        {`Q: ${displayQuestionNumber(inPage, isQuestionNumberingEnabled)} ${text}`}
                      </label>
                      <input id={`${qID}-input`} type="text" />
                    </div>
                  );
                })}
              </form>
              {/* <StickyProgressBar /> */}
              <footer id={`page-footer-container-${surveyId}`} className={pageFooterContainer}>
                {/* <PageNavigation /> */}
                PAGE FOOTER
              </footer>
            </main>
            <If condition={isFooterEnabled}>
              <SurveyFooter
                id={`survey-footer-container-${surveyId}`}
                isGrayScale={grayscale}
                showPrivacyCookiePolicy
              />
            </If>
            {/* </ProgressionProvider> */}
            {/* </PageProvider> */}
          </Layout>
          {/* </SurveyProvider> */}
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default SurveyTaking;
